import { CertificationCountdown } from "../components/CertificationCountdown";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { Sparkles, AlertCircle, Clock, Settings, MapPin, UserRound } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, floatingSheet, emptyFrame } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { StatusTag } from "../components/Drawing";
import { api } from "../api/client";
import { EntityTagsRow } from "../components/EntityTags";
import { PortalLink } from "../components/PortalLink";
import { GRADE_STYLE } from "../lib/consultantGrades";
import { frameworkLabel } from "../nav/frameworkMeta";
import { toDateInput, fmtDateOnly, todayInput } from "../lib/dateOnly";

import { cbPalette, cbSafe } from "../lib/colourBlind";
// ── Types ─────────────────────────────────────────────────────────────────

interface Bureau {
  id: string;
  name: string;
  accreditation: string | null;
  country: string | null;
}

// One certification bureau per standard, each with its own contract lock
// (engaged on My Services).
interface Engagement {
  frameworkId: string;
  bureau: Bureau;
  lockedUntil: string | null;
  locked: boolean;
}

interface ScheduleEvent {
  id: string;
  title: string;
  eventDate: string;
  kind: string;
  notes: string | null;
  frameworkId: string | null;
  isAiGenerated: boolean;
  createdAt: string;
  // Where the task is done and who does it (they get the reminder email).
  siteId: string | null;
  assigneeUserId: string | null;
  site: { id: string; name: string; country: string } | null;
  assignee: { id: string; displayName: string; email: string } | null;
  // Set once the task is marked done; a past event without it is overdue.
  completedAt: string | null;
  /** "<source>:<id>" when generated from a register date (backend lib/registerCalendar.ts). */
  sourceKey?: string | null;
}

// Mirror of backend lib/registerCalendar.ts: a register's task follows the register, so it is
// changed (or removed) there, not here.
const REGISTER_TASK_SOURCES: Record<string, { label: string; href: (id: string) => string }> = {
  policy_review:    { label: "Policy sign-off", href: () => "/policy-governance" },
  supplier_review:  { label: "Supplier register", href: (id) => `/tprm?supplier=${encodeURIComponent(id)}` },
  contract_notice:  { label: "Supplier agreements", href: () => "/tprm" },
  ai_impact_review: { label: "AI systems", href: (id) => `/ai-systems?system=${encodeURIComponent(id)}` },
  bia_review:       { label: "Business impact", href: () => "/resilience/bia" },
  evidence_renewal: { label: "Evidence", href: (id) => `/documents?document=${encodeURIComponent(id)}` },
  change_scheduled: { label: "Change register", href: (id) => `/changes?change=${encodeURIComponent(id)}` },
  dpia_review:      { label: "DPIA register", href: () => "/gdpr/dpia" },
  policy_shortfall: { label: "Policy templates", href: (id) => `/templates?shortfall=${encodeURIComponent(id)}` },
};
function registerTaskSource(key: string | null | undefined) {
  if (!key) return null;
  const i = key.indexOf(":");
  const src = i > 0 ? REGISTER_TASK_SOURCES[key.slice(0, i)] : undefined;
  return src ? { label: src.label, href: src.href(key.slice(i + 1)) } : null;
}

interface SiteRef { id: string; name: string; country: string }
interface MemberRef { id: string; displayName: string; jobTitle: string | null }

interface AiRecommendation {
  title:       string;
  eventDate:   string;
  kind:        KindValue;
  frameworkId: string | null;
  checkKey:    string | null;
  notes:       string;
}

/** What the AI made of the rule-placed schedule (routes/schedule.routes.ts, lib/reviewSchedule.ts). */
interface ScheduleAdvice {
  summary: string | null;
  moved:   number;
  note:    string | null;
}

interface ConsultancySlot {
  id: string;
  grade: string;
  slotDate: string;
  hours: number;
  note: string | null;
  status: string;
  costPence: number;
}

interface GradeAvail {
  totalHours: number;
  bookedHours: number;
  remainingHours: number;
}

interface SlotData {
  slots: ConsultancySlot[];
  availability: Record<string, Record<string, GradeAvail>>;
  hasConsultancy: boolean;
  capacitySet: boolean;
}

type CheckCategory = "assessment" | "evidence" | "policy";

interface ComplianceCheck {
  checkKey:     string;
  frameworkId:  string | null;
  label:        string;
  category:     CheckCategory;
  defaultDays:  number;
  effectiveDays: number;
  overrideDays: number | null;
  lastDoneAt:   string | null;
  nextDueAt:    string;
  isOverdue:    boolean;
  isCustom:     boolean;
  configId:     string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────

const KIND_CONFIG: Record<string, { color: string; bg: string; label: string }> = cbPalette({
  review:  { color: "#3b82f6", bg: "#3b82f622", label: "Review"  },
  audit:   { color: "#a855f7", bg: "#a855f722", label: "Audit"   },
  renewal: { color: "#22c55e", bg: "#22c55e22", label: "Renewal" },
  meeting: { color: "#6b7280", bg: "#6b728022", label: "Meeting" },
});

const KIND_OPTIONS = [
  { value: "review",  label: "Review"  },
  { value: "audit",   label: "Audit"   },
  { value: "renewal", label: "Renewal" },
  { value: "meeting", label: "Meeting" },
] as const;

// Grade colours shared with the consultant pages (lib/consultantGrades.ts). Prices are
// never shown from here: the consultancy's rate card is on Book a consultant.
const GRADE_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  senior:     GRADE_STYLE.senior,
  consultant: GRADE_STYLE.consultant,
  junior:     GRADE_STYLE.junior,
};

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CHECK_CATEGORY_CONFIG: Record<CheckCategory, { color: string; bg: string; label: string }> = {
  assessment: { color: "#7c3aed", bg: "#7c3aed18", label: "Assessment" },
  evidence:   { color: "#0072B2", bg: "#0072B218", label: "Evidence"   },
  policy:     { color: "#E69F00", bg: "#E69F0018", label: "Policy"     },
};

const FRAMEWORK_ROUTES: Record<string, string> = {
  iso27001:              "/mandatory-clauses",
  iso9001:               "/iso9001",
  iso42001:              "/iso42001",
  soc2:                  "/soc2",
  cyber_essentials:      "/cyber-essentials",
  cyber_essentials_plus: "/cyber-essentials-plus",
  pci_dss:               "/pci-dss/saq-a",
  iasme_ca_sole_trader:  "/iasme-ca-sole-trader",
  iasme_ca_micro:        "/iasme-ca-micro",
  iasme_ca_small:        "/iasme-ca-small",
  iasme_ca_medium_large: "/iasme-ca-medium-large",
};

const FREQUENCY_PRESETS = [
  { label: "Monthly (30 days)",      days: 30  },
  { label: "Quarterly (90 days)",    days: 90  },
  { label: "Bi-annually (180 days)", days: 180 },
  { label: "Annually (365 days)",    days: 365 },
  { label: "Every 2 years",          days: 730 },
];

// ── Helpers ───────────────────────────────────────────────────────────────

function isoDate(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function monthKey(y: number, m: number) {
  return `${y}-${String(m).padStart(2, "0")}`;
}

function buildCalendarGrid(year: number, month: number): (number | null)[][] {
  const firstDay = new Date(year, month - 1, 1);
  const totalDays = new Date(year, month, 0).getDate();
  const startDow = (firstDay.getDay() + 6) % 7; // Mon=0 … Sun=6
  const cells: (number | null)[] = Array(startDow).fill(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDayFull(dateStr: string) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function isOverdue(iso: string) {
  return new Date(iso) < new Date();
}

function isPast(dateStr: string) {
  return new Date(dateStr + "T23:59:59") < new Date();
}

// An event's date and a check's due date are calendar dates (lib/dateOnly.ts): keyed, filled and
// labelled by their UTC date.
const toDateInputValue = (iso: string) => toDateInput(iso);

function fmtHours(h: number) {
  const days = Math.floor(h / 8);
  const hrs  = h % 8;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  if (hrs  > 0) parts.push(`${hrs} hr${hrs  !== 1 ? "s" : ""}`);
  return parts.join(" + ") || "0 hrs";
}

function monthLabel(year: number, month: number) {
  return new Date(year, month - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

type KindValue = "review" | "audit" | "renewal" | "meeting";
interface FormState { title: string; eventDate: string; kind: KindValue; notes: string; siteId: string; assigneeUserId: string; }
const EMPTY_FORM: FormState = { title: "", eventDate: "", kind: "review", notes: "", siteId: "", assigneeUserId: "" };

/** Body for POST / PATCH /schedule from a form. */
function eventBody(f: FormState, notesWhenEmpty: null | undefined) {
  return {
    title: f.title.trim(), eventDate: f.eventDate, kind: f.kind, notes: f.notes || notesWhenEmpty,
    siteId: f.siteId || null, assigneeUserId: f.assigneeUserId || null,
  };
}

// Site + person on every event form. Sites come from the Sites page; the
// person must be a member of the organisation and is emailed a week before and
// on the day (worker schedule-reminder job).
function SiteAssigneeFields({ form, onChange, sites, members, compact }: {
  form: FormState; onChange: (patch: Partial<FormState>) => void; sites: SiteRef[]; members: MemberRef[]; compact?: boolean;
}) {
  if (sites.length === 0 && members.length === 0) return null;
  const style = { ...inputStyle, cursor: "pointer", ...(compact ? { fontSize: 12 } : {}) };
  const label = (text: string) => compact ? null : <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>{text}</label>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: compact ? 8 : 12 }}>
      {sites.length > 0 && (
        <div>
          {label("Site")}
          <select style={style} value={form.siteId} onChange={(e) => onChange({ siteId: e.target.value })} title="Where this task is done">
            <option value="">{compact ? "Site (optional)" : "Organisation-wide"}</option>
            {sites.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.country}</option>)}
          </select>
        </div>
      )}
      {members.length > 0 && (
        <div>
          {label("Person responsible")}
          <select style={style} value={form.assigneeUserId} onChange={(e) => onChange({ assigneeUserId: e.target.value })} title="Reminded by email a week before and on the day">
            <option value="">{compact ? "Person (optional)" : "No one named"}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.displayName}{m.jobTitle ? ` — ${m.jobTitle}` : ""}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function EventWhoWhere({ ev }: { ev: ScheduleEvent }) {
  if (!ev.site && !ev.assignee) return null;
  const chip: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: theme.color.panelAlt, color: theme.color.muted, border: `1px solid ${theme.color.border}` };
  return (
    <>
      {ev.site && <span style={chip} title={ev.site.country}><MapPin size={9} />{ev.site.name}</span>}
      {ev.assignee && <span style={chip} title={`${ev.assignee.email} — reminded a week before and on the day`}><UserRound size={9} />{ev.assignee.displayName}</span>}
    </>
  );
}

// ── AI Schedule Modal ─────────────────────────────────────────────────────

function AiScheduleModal({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const [loading, setLoading]             = useState(true);
  const [recommendations, setRecommendations] = useState<AiRecommendation[] | null>(null);
  const [advice, setAdvice]               = useState<ScheduleAdvice | null>(null);
  const [selected, setSelected]           = useState<Set<number>>(new Set());
  const [applying, setApplying]           = useState(false);
  const [error, setError]                 = useState("");

  async function generate() {
    setLoading(true); setError(""); setRecommendations(null); setAdvice(null);
    try {
      const data = await api.post<{ recommendations: AiRecommendation[]; advice?: ScheduleAdvice }>("/schedule/ai-recommendations", {});
      setRecommendations(data.recommendations);
      setAdvice(data.advice ?? null);
      setSelected(new Set(data.recommendations.map((_, i) => i)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to generate recommendations. Please try again.");
    } finally { setLoading(false); }
  }

  async function applySelected() {
    if (!recommendations || selected.size === 0) return;
    setApplying(true); setError("");
    try {
      const toCreate = [...selected].sort((a, b) => a - b).map((i) => ({
        title:        recommendations[i].title,
        eventDate:    recommendations[i].eventDate,
        kind:         recommendations[i].kind,
        notes:        recommendations[i].notes || null,
        frameworkId:  recommendations[i].frameworkId,
        isAiGenerated: true,
      }));
      // /schedule/bulk-create takes at most 100 events per call, and a full
      // twelve-month schedule across several standards comfortably exceeds that
      // — sending the lot in one request failed the whole apply with a raw
      // validation error. Send it in batches instead.
      const BATCH = 100;
      for (let i = 0; i < toCreate.length; i += BATCH) {
        await api.post("/schedule/bulk-create", toCreate.slice(i, i + BATCH));
      }
      onApplied();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save events. Please try again.");
    } finally { setApplying(false); }
  }

  useEffect(() => { generate(); }, []);

  const byFramework: Record<string, number[]> = {};
  if (recommendations) {
    recommendations.forEach((r, i) => {
      const key = r.frameworkId ?? "_general";
      (byFramework[key] ??= []).push(i);
    });
  }

  const allSelected   = recommendations ? selected.size === recommendations.length : false;
  const noneSelected  = selected.size === 0;

  function toggleAll() {
    if (!recommendations) return;
    setSelected(allSelected ? new Set() : new Set(recommendations.map((_, i) => i)));
  }

  function toggle(i: number) {
    setSelected((prev) => { const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next; });
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div style={{ ...floatingSheet, width: "100%", maxWidth: 680 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid var(--c-line)" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkles size={16} color="#7c3aed" /> AI Review Schedule
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3 }}>
              Recommended events based on your enrolled standards and compliance activity
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: theme.color.faint, lineHeight: 1, padding: "0 4px" }}>×</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 8 }}>Generating your personalised review schedule…</div>
              <div style={{ fontSize: 11, color: theme.color.faint }}>This may take a few seconds</div>
            </div>
          )}

          {!loading && error && (
            <div style={{ background: cbSafe("#ef444418"), border: cbSafe("1px solid #ef444440"), borderRadius: 3, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>
              <button onClick={generate} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px" }}>Try again</button>
            </div>
          )}

          {!loading && recommendations && recommendations.length === 0 && (
            <div style={{ fontSize: 13, color: theme.color.subtle, textAlign: "center", padding: "24px 0" }}>No recommendations generated. Ensure at least one framework is enrolled.</div>
          )}

          {!loading && recommendations && recommendations.length > 0 && (
            <>
              {advice && (advice.summary || advice.note) && (
                <div style={{ border: "1px solid var(--c-line)", borderRadius: 3, background: theme.color.panelAlt, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: theme.color.muted }}>
                  {advice.summary && <div>{advice.summary}</div>}
                  {advice.summary && <div style={{ marginTop: 4, color: theme.color.subtle }}>The dates follow the platform's scheduling rules; the AI moved {advice.moved} of them, with its reason in each event's notes.</div>}
                  {advice.note && <div>{advice.note}</div>}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: theme.color.subtle }}>
                  {selected.size} of {recommendations.length} event{recommendations.length !== 1 ? "s" : ""} selected
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={toggleAll} style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer" }}>
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                  <button onClick={generate} style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer" }}>
                    Regenerate
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 440, overflowY: "auto", marginBottom: 16 }}>
                {Object.entries(byFramework).map(([fwKey, indices]) => {
                  const fwLabel = fwKey === "_general" ? "General" : frameworkLabel(fwKey);
                  return (
                    <div key={fwKey} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{fwLabel}</div>
                      {indices.map((i) => {
                        const rec = recommendations[i];
                        const cfg = KIND_CONFIG[rec.kind] ?? KIND_CONFIG.review;
                        const isSelected = selected.has(i);
                        return (
                          <label key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 3, border: `1px solid ${isSelected ? cfg.color + "60" : theme.color.border}`, background: isSelected ? cfg.bg : theme.color.panelAlt, cursor: "pointer", marginBottom: 4, transition: "all 0.15s" }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggle(i)} style={{ marginTop: 2, cursor: "pointer", accentColor: cfg.color }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{rec.title}</span>
                                <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                              </div>
                              <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: rec.notes ? 4 : 0 }}>
                                {new Date(rec.eventDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
                              </div>
                              {rec.notes && <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.4 }}>{rec.notes}</div>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 10 }}>{error}</div>}

              <div style={{ display: "flex", gap: 10, paddingTop: 4, borderTop: `1px solid ${theme.color.border}` }}>
                <button onClick={applySelected} disabled={applying || noneSelected} style={{ ...btnPrimary, opacity: applying || noneSelected ? 0.6 : 1 }}>
                  {applying ? "Adding to calendar…" : `Add ${selected.size} event${selected.size !== 1 ? "s" : ""} to calendar`}
                </button>
                <button onClick={onClose} style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "8px 16px", fontSize: 13, color: theme.color.muted, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Certification Pipeline ────────────────────────────────────────────────
// Read-only: which bureau holds each standard and until when. Bureaux are
// engaged per standard on My Services, so the picker lives there.

function CertificationPipeline({ engagements, onManage }: { engagements: Engagement[]; onManage: () => void }) {
  const sectionHeader = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Certification Pipeline
      </div>
      {/* Nothing to manage while every engagement is locked to its bureau (the Jersey package's bureau
          is part of the price): the button would lead to a page that cannot change it (AUD-48). */}
      {engagements.some((e) => !e.locked) && (
        <button
          onClick={onManage}
          style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}
        >
          Manage bureaux
        </button>
      )}
    </div>
  );

  if (engagements.length === 0) {
    return (
      <div style={{ ...card, marginBottom: 28 }}>
        {sectionHeader}
        <div style={{ fontSize: 13, color: theme.color.subtle }}>
          No standard has been put forward for certification yet. Engage a bureau for each standard you want assessed on My Services.
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...card, marginBottom: 28 }}>
      {sectionHeader}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {engagements.map((e) => (
          <div key={e.frameworkId} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "10px 14px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
            <div style={{ minWidth: 140, fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{frameworkLabel(e.frameworkId)}</div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, color: theme.color.text }}>{e.bureau.name}</div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>{[e.bureau.accreditation, e.bureau.country].filter(Boolean).join(" · ")}</div>
            </div>
            {e.locked ? (
              <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 3, fontWeight: 600, background: theme.color.accentBg, color: theme.color.accentText }}>
                Locked until {new Date(e.lockedUntil!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            ) : (
              <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 3, fontWeight: 600, background: theme.color.panel, color: theme.color.muted, border: `1px solid ${theme.color.border}` }}>
                Lock released
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Day Panel ─────────────────────────────────────────────────────────────

function DayPanel({
  dateStr, events, slotData, checks, sites, members, onClose, onReload, onSlotCancelled,
}: {
  dateStr: string;
  events: ScheduleEvent[];
  slotData: SlotData | null;
  checks: ComplianceCheck[];
  sites: SiteRef[];
  members: MemberRef[];
  onClose: () => void;
  onReload: () => void;
  onSlotCancelled: (id: string) => void;
}) {
  const dayEvents = events.filter((e) => toDateInputValue(e.eventDate) === dateStr);
  const daySlots  = slotData?.slots.filter((s) => s.slotDate.startsWith(dateStr)) ?? [];
  const dayChecks = checks.filter((c) => c.nextDueAt.startsWith(dateStr));
  const past      = isPast(dateStr);

  // Quick-add event form
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<FormState>({ ...EMPTY_FORM, eventDate: dateStr });
  const [addErr, setAddErr]   = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // Compliance check state
  const [expandedCheck, setExpandedCheck]     = useState<string | null>(null);
  const [editingFreqKey, setEditingFreqKey]   = useState<string | null>(null);
  const [freqDays, setFreqDays]               = useState(365);
  const [customFreqDays, setCustomFreqDays]   = useState("");
  const [useCustomFreq, setUseCustomFreq]     = useState(false);
  const [savingFreq, setSavingFreq]           = useState(false);
  const [markingDone, setMarkingDone]         = useState<string | null>(null);
  const [showAddCustom, setShowAddCustom]     = useState(false);
  const [customLabel, setCustomLabel]         = useState("");
  const [customDays, setCustomDays]           = useState(365);
  const [customFirstDue, setCustomFirstDue]   = useState(dateStr);
  const [addingCustom, setAddingCustom]       = useState(false);

  const [cancellingId, setCancellingId] = useState<string | null>(null);

  async function handleAddEvent(e: React.FormEvent) {
    e.preventDefault();
    setAddErr("");
    if (!addForm.title.trim()) { setAddErr("Title is required."); return; }
    setAddSaving(true);
    try {
      await api.post("/schedule", { ...eventBody(addForm, undefined), eventDate: dateStr });
      setShowAdd(false);
      setAddForm({ ...EMPTY_FORM, eventDate: dateStr });
      onReload();
    } catch { setAddErr("Failed to create event. Please try again."); }
    finally { setAddSaving(false); }
  }

  async function handleCancelSlot(slotId: string) {
    if (!confirm("Cancel this booked slot?")) return;
    setCancellingId(slotId);
    try {
      await api.del(`/billing/slots/${slotId}`);
      onSlotCancelled(slotId);
    } catch { alert("Could not cancel slot. Please try again."); }
    finally { setCancellingId(null); }
  }

  async function handleMarkDone(checkKey: string) {
    setMarkingDone(checkKey);
    try {
      await api.post("/schedule/compliance-checks/done", { checkKey });
      onReload();
    } catch { alert("Could not mark as done. Please try again."); }
    finally { setMarkingDone(null); }
  }

  async function handleSaveFrequency(checkKey: string) {
    setSavingFreq(true);
    const days = useCustomFreq ? Math.max(1, parseInt(customFreqDays) || 365) : freqDays;
    try {
      await api.patch(`/schedule/compliance-checks/${encodeURIComponent(checkKey)}`, { overrideDays: days });
      setEditingFreqKey(null);
      onReload();
    } catch { alert("Could not save frequency. Please try again."); }
    finally { setSavingFreq(false); }
  }

  async function handleResetFrequency(checkKey: string) {
    try {
      await api.patch(`/schedule/compliance-checks/${encodeURIComponent(checkKey)}`, { overrideDays: null });
      onReload();
    } catch { alert("Could not reset frequency."); }
  }

  async function handleAddCustomCheck(e: React.FormEvent) {
    e.preventDefault();
    if (!customLabel.trim()) return;
    setAddingCustom(true);
    try {
      await api.post("/schedule/compliance-checks/custom", {
        label: customLabel.trim(),
        frequencyDays: customDays,
        firstDueAt: customFirstDue || undefined,
      });
      setCustomLabel(""); setCustomDays(365); setCustomFirstDue(dateStr);
      setShowAddCustom(false);
      onReload();
    } catch { alert("Could not add custom check. Please try again."); }
    finally { setAddingCustom(false); }
  }

  async function handleDeleteCustom(configId: string) {
    if (!confirm("Delete this custom check?")) return;
    try {
      await api.del(`/schedule/compliance-checks/custom/${configId}`);
      onReload();
    } catch { alert("Could not delete check. Please try again."); }
  }

  return (
    <div style={{ ...card, marginBottom: 20, borderTop: `3px solid ${theme.color.accent}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>
          {formatDayFull(dateStr)}
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: theme.color.faint, lineHeight: 1, padding: "0 4px" }}>
          ×
        </button>
      </div>

      {/* Events on this day */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          Events ({dayEvents.length})
        </div>
        {dayEvents.length === 0 ? (
          <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 8 }}>No events on this day.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {dayEvents.map((ev) => {
              const cfg2 = KIND_CONFIG[ev.kind] ?? KIND_CONFIG.meeting;
              return (
                <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
                  <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, background: cfg2.bg, color: cfg2.color }}>{cfg2.label}</span>
                  <span style={{ fontSize: 13, color: theme.color.text, flex: 1 }}>{ev.title}</span>
                  <EventWhoWhere ev={ev} />
                  {ev.notes && <span style={{ fontSize: 11, color: theme.color.subtle, marginLeft: 4 }}>{ev.notes}</span>}
                </div>
              );
            })}
          </div>
        )}
        {!showAdd ? (
          <button onClick={() => setShowAdd(true)}
            style={{ fontSize: 12, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            + Add event for this day
          </button>
        ) : (
          <form onSubmit={handleAddEvent} style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            <input style={{ ...inputStyle, fontSize: 12 }} value={addForm.title}
              onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Event title *" autoFocus />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <select style={{ ...inputStyle, fontSize: 12, cursor: "pointer" }} value={addForm.kind}
                onChange={(e) => setAddForm((f) => ({ ...f, kind: e.target.value as KindValue }))}>
                {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={{ ...inputStyle, fontSize: 12 }} value={addForm.notes}
                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Notes (optional)" />
            </div>
            <SiteAssigneeFields compact form={addForm} onChange={(patch) => setAddForm((f) => ({ ...f, ...patch }))} sites={sites} members={members} />
            {addErr && <div style={{ fontSize: 11, color: theme.color.dangerText }}>{addErr}</div>}
            <div style={{ display: "flex", gap: 6 }}>
              <button type="submit" disabled={addSaving} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: addSaving ? 0.7 : 1 }}>
                {addSaving ? "Saving…" : "Create"}
              </button>
              <button type="button" onClick={() => { setShowAdd(false); setAddErr(""); }}
                style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "6px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Compliance checks due on this day */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Compliance Checks ({dayChecks.length})
          </div>
          <button onClick={() => setShowAddCustom((s) => !s)}
            style={{ fontSize: 11, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            {showAddCustom ? "Cancel" : "+ Custom check"}
          </button>
        </div>

        {showAddCustom && (
          <form onSubmit={handleAddCustomCheck} style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12, marginBottom: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Add custom compliance check</div>
            <input style={{ ...inputStyle, fontSize: 12 }} value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Check label (e.g. Quarterly firewall audit)" autoFocus />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 3 }}>Repeat every</label>
                <select style={{ ...inputStyle, fontSize: 12, cursor: "pointer" }} value={customDays} onChange={(e) => setCustomDays(Number(e.target.value))}>
                  {FREQUENCY_PRESETS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 3 }}>First due date</label>
                <input type="date" style={{ ...inputStyle, fontSize: 12 }} value={customFirstDue} onChange={(e) => setCustomFirstDue(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="submit" disabled={addingCustom || !customLabel.trim()} style={{ ...btnPrimary, fontSize: 12, padding: "6px 14px", opacity: addingCustom || !customLabel.trim() ? 0.7 : 1 }}>
                {addingCustom ? "Adding…" : "Add check"}
              </button>
            </div>
          </form>
        )}

        {dayChecks.length === 0 ? (
          <div style={{ fontSize: 12, color: theme.color.faint }}>No compliance checks due on this day.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {dayChecks.map((chk) => {
              const cat  = CHECK_CATEGORY_CONFIG[chk.category];
              const clr  = chk.isOverdue ? theme.color.dangerText : cat.color;
              const bg   = chk.isOverdue ? "#D55E0018"   : cat.bg;
              const isExpanded = expandedCheck === chk.checkKey;
              const isEditingFreq = editingFreqKey === chk.checkKey;
              const fwLabel = chk.frameworkId ? frameworkLabel(chk.frameworkId) : null;

              return (
                <div key={chk.checkKey} style={{ border: `1px solid ${clr}40`, borderRadius: 3, background: bg, overflow: "hidden" }}>
                  <div onClick={() => setExpandedCheck(isExpanded ? null : chk.checkKey)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: clr, marginBottom: 2 }}>{chk.label}</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        {fwLabel && <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, background: theme.color.panelAlt, padding: "1px 6px", borderRadius: 3 }}>{fwLabel}</span>}
                        <span style={{ fontSize: 10, fontWeight: 700, color: cat.color, background: `${cat.color}20`, padding: "1px 6px", borderRadius: 3 }}>{cat.label}</span>
                        {chk.isOverdue && <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.dangerText }}>OVERDUE</span>}
                        <span style={{ fontSize: 10, color: theme.color.faint }}>every {chk.effectiveDays} days</span>
                        {chk.overrideDays !== null && <span style={{ fontSize: 10, color: theme.color.accentText, display: "inline-flex", alignItems: "center", gap: 3 }}><Settings size={10} /> custom frequency</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 13, color: theme.color.faint, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: "0 12px 12px", borderTop: `1px solid ${clr}30` }}>
                      <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 10, marginBottom: 10 }}>
                        <div>Last completed: <strong>{chk.lastDoneAt ? new Date(chk.lastDoneAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Never"}</strong></div>
                        {!chk.isCustom && chk.overrideDays !== null && (
                          <div style={{ marginTop: 3 }}>
                            Standard frequency: {chk.defaultDays} days · Your override: {chk.overrideDays} days
                          </div>
                        )}
                      </div>

                      {/* Frequency editor */}
                      {isEditingFreq ? (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: theme.color.muted, marginBottom: 6 }}>Set check frequency:</div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                            <select style={{ ...inputStyle, fontSize: 12, cursor: "pointer", width: "auto" }}
                              value={useCustomFreq ? "custom" : String(freqDays)}
                              onChange={(e) => { if (e.target.value === "custom") { setUseCustomFreq(true); } else { setUseCustomFreq(false); setFreqDays(Number(e.target.value)); } }}>
                              {FREQUENCY_PRESETS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
                              <option value="custom">Custom…</option>
                            </select>
                            {useCustomFreq && (
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <input type="number" min={1} style={{ ...inputStyle, fontSize: 12, width: 64 }} value={customFreqDays} onChange={(e) => setCustomFreqDays(e.target.value)} placeholder="days" />
                                <span style={{ fontSize: 12, color: theme.color.muted }}>days</span>
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => handleSaveFrequency(chk.checkKey)} disabled={savingFreq}
                              style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", opacity: savingFreq ? 0.7 : 1 }}>
                              {savingFreq ? "Saving…" : "Save"}
                            </button>
                            <button onClick={() => setEditingFreqKey(null)}
                              style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer" }}>
                              Cancel
                            </button>
                            {!chk.isCustom && chk.overrideDays !== null && (
                              <button onClick={() => handleResetFrequency(chk.checkKey)}
                                style={{ background: "none", border: "none", fontSize: 11, color: theme.color.faint, cursor: "pointer", textDecoration: "underline" }}>
                                Reset to standard
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button onClick={() => handleMarkDone(chk.checkKey)} disabled={markingDone === chk.checkKey}
                            style={{ background: "#0072B218", border: `1px solid ${theme.color.success}40`, borderRadius: 3, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: theme.color.success, cursor: "pointer", opacity: markingDone === chk.checkKey ? 0.7 : 1 }}>
                            {markingDone === chk.checkKey ? "Saving…" : "✓ Mark as done"}
                          </button>
                          <button onClick={() => { setEditingFreqKey(chk.checkKey); setFreqDays(chk.effectiveDays); setUseCustomFreq(false); setCustomFreqDays(""); }}
                            style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "6px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                            Change frequency
                          </button>
                          {chk.isCustom && chk.configId && (
                            <button onClick={() => handleDeleteCustom(chk.configId!)}
                              style={{ background: "none", border: "none", fontSize: 12, color: theme.color.dangerText, cursor: "pointer", textDecoration: "underline" }}>
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Consultant slots */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          Consultant Time
        </div>

        {!slotData?.hasConsultancy && (
          <div style={{ fontSize: 12, color: theme.color.subtle }}>
            Your organisation has not been assigned a consultancy yet. Contact your consultant to get started.
          </div>
        )}

        {slotData?.hasConsultancy && !slotData.capacitySet && (
          <div style={{ fontSize: 12, color: theme.color.subtle }}>
            Your consultancy hasn't published availability yet. Check back soon or contact them directly.
          </div>
        )}

        {slotData?.hasConsultancy && slotData.capacitySet && (
          <>
            {/* This organisation's bookings on the day. Booking itself happens on
                Book a consultant (/consultancy-time), which picks the consultant and
                reads their real availability — this panel used to carry its own form
                with a grade-keyed availability shape the API no longer returns. */}
            {daySlots.length > 0 ? (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 6 }}>Your bookings on this day:</div>
                {daySlots.map((s) => {
                  const gcfg = GRADE_CONFIG[s.grade] ?? GRADE_CONFIG.consultant;
                  const canCancel = !past && (s.status === "confirmed" || s.status === "pending");
                  return (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: gcfg.bg, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: gcfg.color }}>{gcfg.label}</span>
                      <span style={{ fontSize: 12, color: theme.color.text, flex: 1 }}>{fmtHours(s.hours)}{s.status === "pending" ? " · awaiting confirmation" : ""}</span>
                      <span style={{ fontSize: 12, color: theme.color.subtle }}>£{(s.costPence / 100).toFixed(0)}</span>
                      {s.note && <span style={{ fontSize: 11, color: theme.color.faint }}>— {s.note}</span>}
                      {canCancel && (
                        <button onClick={() => handleCancelSlot(s.id)} disabled={cancellingId === s.id}
                          style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "2px 7px", fontSize: 11, color: theme.color.dangerText, cursor: "pointer", opacity: cancellingId === s.id ? 0.5 : 1 }}>
                          {cancellingId === s.id ? "…" : "Cancel"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10 }}>No consultant time booked on this day.</div>
            )}
            {past ? (
              <div style={{ fontSize: 12, color: theme.color.faint }}>This date has passed — no new bookings can be made.</div>
            ) : (
              <PortalLink to={`/consultancy-time?date=${dateStr}`} style={{ ...btnPrimary, display: "inline-block", fontSize: 12, padding: "7px 16px", textDecoration: "none" }}>
                Book consultant time for this day
              </PortalLink>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Calendar Grid ─────────────────────────────────────────────────────────

function CalendarGrid({
  year, month, events, slotData, checks, selectedDay, onSelectDay,
}: {
  year:        number;
  month:       number;
  events:      ScheduleEvent[];
  slotData:    SlotData | null;
  checks:      ComplianceCheck[];
  selectedDay: string | null;
  onSelectDay: (d: string | null) => void;
}) {
  const grid   = buildCalendarGrid(year, month);
  const today  = todayInput();

  // Group events by YYYY-MM-DD key
  const eventsByDate: Record<string, ScheduleEvent[]> = {};
  for (const ev of events) {
    const k = toDateInputValue(ev.eventDate);
    (eventsByDate[k] ??= []).push(ev);
  }

  // Group slots by date key
  const slotsByDate: Record<string, ConsultancySlot[]> = {};
  for (const s of slotData?.slots ?? []) {
    const k = s.slotDate.slice(0, 10);
    (slotsByDate[k] ??= []).push(s);
  }

  // Group checks by next due date
  const checksByDate: Record<string, ComplianceCheck[]> = {};
  for (const c of checks) {
    const k = c.nextDueAt.slice(0, 10);
    (checksByDate[k] ??= []).push(c);
  }

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      {/* Day-of-week headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {DOW_LABELS.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: theme.color.faint, padding: "4px 0", letterSpacing: "0.04em" }}>{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {grid.map((row, ri) => (
          <div key={ri} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {row.map((day, ci) => {
              if (!day) return <div key={ci} />;
              const key    = isoDate(year, month, day);
              const isToday   = key === today;
              const isSelected = key === selectedDay;
              const dayEvents  = eventsByDate[key] ?? [];
              const daySlots   = slotsByDate[key]  ?? [];
              const pastDay    = key < today;

              return (
                <button
                  key={ci}
                  onClick={() => onSelectDay(isSelected ? null : key)}
                  style={{
                    padding: "6px 4px",
                    borderRadius: 3,
                    border: isToday
                      ? `2px solid ${theme.color.accent}`
                      : isSelected
                        ? `2px solid ${theme.color.accentText}`
                        : `1px solid ${theme.color.border}`,
                    background: isSelected ? theme.color.accentBg : isToday ? `${theme.color.accent}10` : theme.color.panel,
                    cursor: "pointer",
                    textAlign: "left",
                    minHeight: 64,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    opacity: pastDay ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: isToday ? 800 : 600, color: isToday ? theme.color.accentText : isSelected ? theme.color.heading : theme.color.muted, lineHeight: 1 }}>
                    {day}
                  </div>
                  {/* Event dots — filled for manual, outlined ring for AI-generated */}
                  {dayEvents.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 1 }}>
                      {dayEvents.slice(0, 4).map((ev) => {
                        const ecfg = KIND_CONFIG[ev.kind] ?? KIND_CONFIG.meeting;
                        return ev.isAiGenerated
                          ? <div key={ev.id} title={`AI: ${ev.title}`} style={{ width: 7, height: 7, borderRadius: "50%", border: `1.5px solid ${ecfg.color}`, background: "transparent", flexShrink: 0 }} />
                          : <div key={ev.id} title={ev.title}        style={{ width: 6, height: 6, borderRadius: "50%", background: ecfg.color, flexShrink: 0 }} />;
                      })}
                      {dayEvents.length > 4 && <div style={{ fontSize: 8, color: theme.color.faint, lineHeight: "6px" }}>+{dayEvents.length - 4}</div>}
                    </div>
                  )}
                  {/* Slot chips */}
                  {daySlots.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 1 }}>
                      {daySlots.slice(0, 2).map((s) => {
                        const gcfg = GRADE_CONFIG[s.grade] ?? GRADE_CONFIG.consultant;
                        return (
                          <div key={s.id} style={{ fontSize: 9, fontWeight: 700, color: gcfg.color, background: gcfg.bg, borderRadius: 3, padding: "1px 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {gcfg.label.slice(0, 3).toUpperCase()} {s.hours >= 8 ? `${s.hours / 8}d` : `${s.hours}h`}
                          </div>
                        );
                      })}
                      {daySlots.length > 2 && <div style={{ fontSize: 9, color: theme.color.faint }}>+{daySlots.length - 2}</div>}
                    </div>
                  )}
                  {/* Compliance check indicators */}
                  {(() => {
                    const dayChecks = checksByDate[key] ?? [];
                    if (dayChecks.length === 0) return null;
                    const hasOverdue = dayChecks.some((c) => c.isOverdue);
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 1 }}>
                        {dayChecks.slice(0, 3).map((c) => {
                          const ccfg = CHECK_CATEGORY_CONFIG[c.category];
                          const dotColor = c.isOverdue ? theme.color.dangerText : ccfg.color;
                          return (
                            <div key={c.checkKey} title={c.label}
                              style={{ width: 7, height: 7, borderRadius: 2, background: dotColor, flexShrink: 0 }} />
                          );
                        })}
                        {dayChecks.length > 3 && <div style={{ fontSize: 8, color: hasOverdue ? theme.color.dangerText : theme.color.faint, lineHeight: "7px" }}>+{dayChecks.length - 3}</div>}
                      </div>
                    );
                  })()}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", paddingTop: 12, borderTop: `1px solid ${theme.color.border}`, marginTop: 10 }}>
        {/* Event kinds */}
        {Object.entries(KIND_CONFIG).map(([kind, cfg]) => (
          <div key={kind} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: theme.color.subtle }}>{cfg.label}</span>
          </div>
        ))}
        {/* AI event ring */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", border: `1.5px solid ${KIND_CONFIG.review.color}`, background: "transparent", flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: theme.color.subtle }}>AI scheduled</span>
        </div>
        {/* Compliance check categories */}
        {Object.entries(CHECK_CATEGORY_CONFIG).map(([cat, cfg]) => (
          <div key={cat} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: theme.color.subtle }}>{cfg.label} due</span>
          </div>
        ))}
        {/* Overdue check */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: 2, background: theme.color.dangerText, flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: theme.color.subtle }}>Overdue check</span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export function SchedulePage() {
  const navigate = usePortalNavigate();
  // Calendar navigation
  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Data
  const [events,    setEvents]    = useState<ScheduleEvent[] | null>(null);
  const [slotData,  setSlotData]  = useState<SlotData | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [checks,    setChecks]    = useState<ComplianceCheck[]>([]);
  const [kindFilter, setKindFilter] = useState("all");
  // Sites and members for the event forms; ?site=<id> (from a site's detail
  // page) opens the list filtered to that site's tasks.
  const [searchParams]            = useSearchParams();
  const [sites,     setSites]     = useState<SiteRef[]>([]);
  const [members,   setMembers]   = useState<MemberRef[]>([]);
  // ?event=<id> (tag cross-references, a site's detail page, reminders) brings
  // that task into view in the list and turns the calendar to its month.
  const eventParam = searchParams.get("event");
  const [siteFilter, setSiteFilter] = useState(() => eventParam ? "all" : searchParams.get("site") ?? "all");
  const linkedEvent = eventParam ? events?.find((e) => e.id === eventParam) ?? null : null;
  useEffect(() => {
    if (!linkedEvent) return;
    const d = new Date(linkedEvent.eventDate);
    setViewYear(d.getUTCFullYear()); setViewMonth(d.getUTCMonth() + 1);
  }, [linkedEvent?.id]);
  useDeepLinkScroll(linkedEvent?.id, !!events);

  // AI schedule modal
  const [showAiModal, setShowAiModal] = useState(false);

  // New event form (global)
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState("");

  // Inline edit state
  const [editingId, setEditingId]     = useState<string | null>(null);
  const [editForm, setEditForm]       = useState<FormState>(EMPTY_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError]     = useState("");

  // GDPR regulatory deadlines
  const [gdprDeadlines, setGdprDeadlines] = useState<Array<{
    id: string; type: string; title: string; dueAt: string; isOverdue: boolean; hoursRemaining: number | null; severity?: string;
  }>>([]);

  // Policy document recommendations
  const [policyRecs, setPolicyRecs] = useState<Array<{ documentId: string; documentName: string; phrase: string; label: string; intervalDays: number }>>([]);
  const [showPolicyRec, setShowPolicyRec] = useState(false);

  // Certification pipeline
  const [engagements, setEngagements] = useState<Engagement[]>([]);

  const loadChecks = useCallback(async () => {
    try {
      const data = await api.get<{ checks: ComplianceCheck[] }>("/schedule/compliance-checks");
      setChecks(data.checks);
    } catch { /* non-fatal: no frameworks enrolled yet */ }
  }, []);

  const loadEvents = useCallback(async () => {
    const [eventsData, profileData] = await Promise.all([
      api.get<{ events: ScheduleEvent[] }>("/schedule"),
      api.get<{ organization: { engagements: Engagement[] } }>("/organizations/profile"),
    ]);
    setEvents(eventsData.events);
    setEngagements(profileData.organization.engagements ?? []);
  }, []);

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    try {
      const data = await api.get<SlotData>(`/billing/slots?month=${monthKey(viewYear, viewMonth)}`);
      setSlotData(data);
    } catch { /* Non-fatal — no consultancy is fine */ }
    finally { setSlotsLoading(false); }
  }, [viewYear, viewMonth]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadSlots(); }, [loadSlots]);
  useEffect(() => { loadChecks(); }, [loadChecks]);
  useEffect(() => {
    // Data protection deadlines only where the organisation has the module (never in an edition
    // that leaves it out, where the API answers 404).
    api.get<{ dataProtection?: { enabled?: boolean }; editionExcludes?: string[] }>("/organizations/addons")
      .then((a) => {
        if (a.editionExcludes?.includes("dataProtection") || a.dataProtection?.enabled === false) return;
        return api.get<{ deadlines: typeof gdprDeadlines }>("/gdpr/upcoming-deadlines").then((d) => setGdprDeadlines(d.deadlines ?? []));
      })
      .catch(() => {});
    api.get<{ recommendations: typeof policyRecs }>("/schedule/policy-recommendations")
      .then((d) => setPolicyRecs(d.recommendations ?? []))
      .catch(() => {});
    // Sites and members feed the event forms; either may be empty and the fields hide.
    api.get<{ sites: SiteRef[] }>("/sites").then((d) => setSites(d.sites)).catch(() => {});
    api.get<{ members: MemberRef[] }>("/organizations/members").then((d) => setMembers(d.members)).catch(() => {});
  }, []);

  function prevMonth() {
    if (viewMonth === 1) { setViewYear((y) => y - 1); setViewMonth(12); }
    else setViewMonth((m) => m - 1);
    setSelectedDay(null);
  }
  function nextMonth() {
    if (viewMonth === 12) { setViewYear((y) => y + 1); setViewMonth(1); }
    else setViewMonth((m) => m + 1);
    setSelectedDay(null);
  }
  function goToday() {
    const t = new Date();
    setViewYear(t.getFullYear()); setViewMonth(t.getMonth() + 1);
    setSelectedDay(todayInput());
  }

  function handleSlotCancelled(id: string) {
    setSlotData((prev) => prev ? { ...prev, slots: prev.slots.filter((s) => s.id !== id) } : prev);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setFormError("");
    if (!form.title.trim()) { setFormError("Title is required."); return; }
    if (!form.eventDate) { setFormError("Date is required."); return; }
    setSubmitting(true);
    try {
      await api.post("/schedule", eventBody(form, undefined));
      setForm(EMPTY_FORM); setShowForm(false); await loadEvents();
    } catch { setFormError("Failed to create event. Please try again."); }
    finally { setSubmitting(false); }
  }

  function startEdit(ev: ScheduleEvent) {
    setEditingId(ev.id);
    setEditForm({ title: ev.title, eventDate: toDateInputValue(ev.eventDate), kind: ev.kind as KindValue, notes: ev.notes ?? "", siteId: ev.siteId ?? "", assigneeUserId: ev.assigneeUserId ?? "" });
    setEditError("");
  }

  async function handleSaveEdit(id: string) {
    setEditError("");
    if (!editForm.title.trim()) { setEditError("Title is required."); return; }
    if (!editForm.eventDate) { setEditError("Date is required."); return; }
    setEditSubmitting(true);
    try {
      await api.patch(`/schedule/${id}`, eventBody(editForm, null));
      setEditingId(null); await loadEvents();
    } catch { setEditError("Failed to save changes. Please try again."); }
    finally { setEditSubmitting(false); }
  }

  async function handleToggleDone(ev: ScheduleEvent) {
    await api.patch(`/schedule/${ev.id}`, { completed: !ev.completedAt });
    await loadEvents();
  }

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"?`)) return;
    await api.del(`/schedule/${id}`);
    await loadEvents();
  }

  const reload = useCallback(async () => { await Promise.all([loadEvents(), loadSlots(), loadChecks()]); }, [loadEvents, loadSlots, loadChecks]);

  if (!events) {
    return <LoadingBlock />;
  }

  const visible = events.filter((e) =>
    (kindFilter === "all" || e.kind === kindFilter) &&
    (siteFilter === "all" || (siteFilter === "none" ? !e.siteId : e.siteId === siteFilter))
  );

  return (
    <div className="page-pad">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Calendar</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Schedule reviews, audits, and book consultant time.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setShowAiModal(true)}
            style={{ ...btnPrimary, background: "linear-gradient(135deg, #7c3aed, #3b82f6)", display: "flex", alignItems: "center", gap: 6 }}>
            <Sparkles size={13} style={{ verticalAlign: "middle" }} /> AI Schedule
          </button>
          <button onClick={() => { setShowForm(!showForm); setForm(EMPTY_FORM); setFormError(""); }} style={btnPrimary}>
            {showForm ? "Cancel" : "+ New event"}
          </button>
        </div>
      </div>

      {showAiModal && (
        <AiScheduleModal
          onClose={() => setShowAiModal(false)}
          onApplied={() => { setShowAiModal(false); reload(); }}
        />
      )}

      {/* GDPR regulatory deadlines */}
      {gdprDeadlines.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            GDPR Regulatory Deadlines
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {gdprDeadlines.map((d) => {
              const overdue  = d.isOverdue;
              const color    = overdue ? cbSafe("#ef4444") : d.hoursRemaining !== null && d.hoursRemaining < 24 ? "#f59e0b" : theme.color.accent;
              const bgColor  = overdue ? cbSafe("#ef444418") : d.hoursRemaining !== null && d.hoursRemaining < 24 ? "#f59e0b18" : theme.color.accentBg;
              const dueLabel = overdue
                ? `Overdue by ${Math.abs(d.hoursRemaining ?? 0)}h`
                : d.hoursRemaining !== null
                ? `Due in ${d.hoursRemaining}h`
                : `Due ${new Date(d.dueAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
              return (
                <div
                  key={`${d.type}-${d.id}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 14px", borderRadius: 3,
                    background: bgColor, border: `1px solid ${color}44`,
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {overdue ? <AlertCircle size={14} color={color} /> : <Clock size={14} color={color} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.title}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color, flexShrink: 0 }}>{dueLabel}</div>
                  <PortalLink
                    to={d.type === "dsr" ? `/gdpr/dsr?request=${d.id}` : `/gdpr/breaches?breach=${d.id}`}
                    style={{ fontSize: 11, color: theme.color.accent, textDecoration: "none", flexShrink: 0, fontWeight: 600 }}
                  >
                    View →
                  </PortalLink>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Certification countdown: readiness tasks back-planned from each standard's next audit (components/CertificationCountdown.tsx) */}
      <CertificationCountdown />

      {/* Certification Pipeline */}
      <CertificationPipeline engagements={engagements} onManage={() => navigate("/my-services")} />

      {/* New event form */}
      {showForm && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>New event</div>
          <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Title *</label>
              <input style={inputStyle} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Annual management review" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Date *</label>
                <input type="date" style={inputStyle} value={form.eventDate} onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Type</label>
                <select style={{ ...inputStyle, cursor: "pointer" }} value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as KindValue }))}>
                  {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <SiteAssigneeFields form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} sites={sites} members={members} />
            <div>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Notes (optional)</label>
              <textarea style={{ ...inputStyle, resize: "vertical" }} rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes…" />
            </div>
            {formError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{formError}</div>}
            <div>
              <button type="submit" disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Saving…" : "Create event"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Calendar navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={prevMonth}
            style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 10px", cursor: "pointer", fontSize: 14, color: theme.color.muted }}>
            ‹
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, minWidth: 160, textAlign: "center" }}>
            {monthLabel(viewYear, viewMonth)}
          </div>
          <button onClick={nextMonth}
            style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 10px", cursor: "pointer", fontSize: 14, color: theme.color.muted }}>
            ›
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {slotsLoading && <span style={{ fontSize: 11, color: theme.color.faint }}>Loading availability…</span>}
          <button onClick={goToday}
            style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "5px 12px", cursor: "pointer", fontSize: 12, color: theme.color.muted }}>
            Today
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
        {Object.entries(KIND_CONFIG).map(([k, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />
            <span style={{ fontSize: 11, color: theme.color.subtle }}>{c.label}</span>
          </div>
        ))}
        {slotData?.hasConsultancy && Object.entries(GRADE_CONFIG).map(([k, g]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: g.color }} />
            <span style={{ fontSize: 11, color: theme.color.subtle }}>{g.label} booking</span>
          </div>
        ))}
        {checks.length > 0 && Object.entries(CHECK_CATEGORY_CONFIG).map(([k, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: 2, background: c.color }} />
            <span style={{ fontSize: 11, color: theme.color.subtle }}>{c.label} due</span>
          </div>
        ))}
      </div>

      {/* Overdue checks banner */}
      {(() => {
        const overdueChecks = checks.filter((c) => c.isOverdue);
        if (overdueChecks.length === 0) return null;
        return (
          <div style={{ background: "#D55E0018", border: `1px solid ${theme.color.dangerText}40`, borderRadius: 3, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.dangerText }}>
              {overdueChecks.length} compliance check{overdueChecks.length !== 1 ? "s" : ""} overdue
            </span>
            <span style={{ fontSize: 12, color: theme.color.dangerText, flex: 1 }}>
              {overdueChecks.slice(0, 2).map((c) => c.label).join(", ")}{overdueChecks.length > 2 ? ` and ${overdueChecks.length - 2} more` : ""}
            </span>
            <button
              onClick={() => navigate("/compliance")}
              style={{ fontSize: 12, fontWeight: 600, color: theme.color.dangerText, background: "transparent", border: `1px solid ${theme.color.dangerText}60`, borderRadius: 3, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Run check now →
            </button>
          </div>
        );
      })()}

      {/* Calendar grid */}
      <CalendarGrid
        year={viewYear}
        month={viewMonth}
        events={events}
        slotData={slotData}
        checks={checks}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
      />

      {/* Day detail panel */}
      {selectedDay && (
        <DayPanel
          dateStr={selectedDay}
          events={events}
          slotData={slotData}
          checks={checks}
          sites={sites}
          members={members}
          onClose={() => setSelectedDay(null)}
          onReload={reload}
          onSlotCancelled={handleSlotCancelled}
        />
      )}

      {/* Event list */}
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14, marginTop: 8 }}>
        All Events
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {[{ value: "all", label: "All" }, ...KIND_OPTIONS].map((opt) => (
          <button key={opt.value} onClick={() => setKindFilter(opt.value)}
            style={{ padding: "6px 13px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${kindFilter === opt.value ? theme.color.accent : theme.color.borderStrong}`, background: kindFilter === opt.value ? theme.color.accentBg : theme.color.panel, color: kindFilter === opt.value ? theme.color.accentText : theme.color.subtle }}>
            {opt.label}
          </button>
        ))}
        {sites.length > 0 && (
          <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} title="Tasks at one site"
            style={{ ...inputStyle, width: "auto", marginLeft: "auto", padding: "6px 10px", fontSize: 12, cursor: "pointer", ...(siteFilter !== "all" ? { borderColor: theme.color.accent, color: theme.color.accentText } : {}) }}>
            <option value="all">All sites</option>
            {sites.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            <option value="none">Organisation-wide only</option>
          </select>
        )}
      </div>

      {visible.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
          {events.length === 0 ? "No events scheduled. Click a day on the calendar or use '+ New event'." : "No events match this filter."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visible.map((ev) => {
            const cfg = KIND_CONFIG[ev.kind] ?? KIND_CONFIG.meeting;
            const done      = !!ev.completedAt;
            const overdue   = !done && isOverdue(ev.eventDate);
            const isEditing = editingId === ev.id;
            return (
              <div key={ev.id} data-deeplink={ev.id} style={{ ...card, borderLeft: cbSafe(`3px solid ${overdue && !isEditing ? "#ef4444" : cfg.color}`), paddingLeft: 14 }}>
                {isEditing ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Edit event</div>
                    <div>
                      <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Title *</label>
                      <input style={inputStyle} value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} autoFocus />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Date *</label>
                        <input type="date" style={inputStyle} value={editForm.eventDate} onChange={(e) => setEditForm((f) => ({ ...f, eventDate: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Type</label>
                        <select style={{ ...inputStyle, cursor: "pointer" }} value={editForm.kind} onChange={(e) => setEditForm((f) => ({ ...f, kind: e.target.value as KindValue }))}>
                          {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <SiteAssigneeFields form={editForm} onChange={(patch) => setEditForm((f) => ({ ...f, ...patch }))} sites={sites} members={members} />
                    <div>
                      <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>Notes</label>
                      <textarea style={{ ...inputStyle, resize: "vertical" }} rows={2} value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes…" />
                    </div>
                    {editError && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{editError}</div>}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => handleSaveEdit(ev.id)} disabled={editSubmitting} style={{ ...btnPrimary, opacity: editSubmitting ? 0.7 : 1 }}>
                        {editSubmitting ? "Saving…" : "Save changes"}
                      </button>
                      <button onClick={() => setEditingId(null)}
                        style={{ background: "transparent", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "8px 14px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: overdue ? theme.color.dangerText : theme.color.heading }}>{ev.title}</span>
                        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                        {ev.isAiGenerated && <span title="AI generated" style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, background: "#7c3aed18", color: "#7c3aed" }}><Sparkles size={9} /> AI</span>}
                        {ev.frameworkId && (
                          <button
                            onClick={e => { e.stopPropagation(); navigate(FRAMEWORK_ROUTES[ev.frameworkId!] ?? "/dashboard"); }}
                            style={{ display: "inline-block", padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}33`, cursor: "pointer" }}
                          >
                            {frameworkLabel(ev.frameworkId)}
                          </button>
                        )}
                        {overdue && <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: cbSafe("#ef444422"), color: cbSafe("#ef4444") }}>Overdue</span>}
                        {done && <StatusTag tone="met" title={`Marked done ${formatDate(ev.completedAt!)}`}>Done</StatusTag>}
                        {registerTaskSource(ev.sourceKey) && (
                          <button
                            onClick={e => { e.stopPropagation(); navigate(registerTaskSource(ev.sourceKey)!.href); }}
                            title="This task follows a date on the register: change or remove it there"
                            style={{ display: "inline-block", padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: "transparent", color: "var(--c-ref)", border: "1px solid var(--c-line)", cursor: "pointer" }}
                          >
                            From {registerTaskSource(ev.sourceKey)!.label}
                          </button>
                        )}
                        <EventWhoWhere ev={ev} />
                      </div>
                      <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: ev.notes ? 6 : 0 }}>{fmtDateOnly(ev.eventDate, { day: "numeric", month: "short", year: "numeric" })}</div>
                      {ev.notes && <div style={{ fontSize: 12, color: theme.color.subtle }}>{ev.notes}</div>}
                      <EntityTagsRow entity={{ kind: "task", id: ev.id }} style={{ marginTop: 6, marginBottom: 0 }} />
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => handleToggleDone(ev)}
                        title={done ? "Reopen this task" : "Mark this task as done — it will no longer count as overdue"}
                        style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.muted, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>
                        {done ? "Reopen" : "Mark done"}
                      </button>
                      {!registerTaskSource(ev.sourceKey) && (
                        <>
                          <button onClick={() => startEdit(ev)}
                            style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.muted, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>
                            Edit
                          </button>
                          <button onClick={() => handleDelete(ev.id, ev.title)}
                            style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.faint, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming compliance checks */}
      {checks.length > 0 && (() => {
        const upcoming = [...checks]
          .sort((a, b) => new Date(a.nextDueAt).getTime() - new Date(b.nextDueAt).getTime())
          .slice(0, 12);
        return (
          <div style={{ marginTop: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
              Upcoming Compliance Checks
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {upcoming.map((chk) => {
                const cat  = CHECK_CATEGORY_CONFIG[chk.category];
                const clr  = chk.isOverdue ? theme.color.dangerText : cat.color;
                const bg   = chk.isOverdue ? "#D55E0018"   : cat.bg;
                const fwLabel = chk.frameworkId ? frameworkLabel(chk.frameworkId) : "Custom";
                const dueDate = new Date(chk.nextDueAt);
                return (
                  <div key={chk.checkKey}
                    style={{ ...card, display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderLeft: `3px solid ${clr}`, paddingLeft: 12 }}>
                    <div style={{ flexShrink: 0, width: 44, textAlign: "center" }}>
                      <div style={{ fontSize: 17, fontWeight: 800, color: clr, lineHeight: 1 }}>{dueDate.getUTCDate()}</div>
                      <div style={{ fontSize: 10, color: theme.color.faint }}>{fmtDateOnly(dueDate, { month: "short", year: "2-digit" })}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: chk.isOverdue ? clr : theme.color.heading }}>{chk.label}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint }}>{fwLabel}</span>
                        <span style={{ fontSize: 10, color: cat.color, background: bg, padding: "1px 6px", borderRadius: 3 }}>{cat.label}</span>
                        <span style={{ fontSize: 10, color: theme.color.faint }}>every {chk.effectiveDays} days</span>
                        {chk.isOverdue && <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.dangerText }}>OVERDUE</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const d = new Date(chk.nextDueAt);
                        const key = isoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
                        setViewYear(d.getFullYear());
                        setViewMonth(d.getMonth() + 1);
                        setSelectedDay(key);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      style={{ background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "4px 10px", fontSize: 11, color: theme.color.muted, cursor: "pointer", flexShrink: 0 }}>
                      View
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Policy Recommendations */}
      {policyRecs.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <button
            onClick={() => setShowPolicyRec((v) => !v)}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 12 }}
          >
            <span style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Policy Recommendations</span>
            <span style={{ fontSize: 12, color: theme.color.muted }}>({policyRecs.length})</span>
            <span style={{ fontSize: 11, color: theme.color.muted }}>{showPolicyRec ? "▲" : "▼"}</span>
          </button>
          {showPolicyRec && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {policyRecs.map((rec, i) => (
                <div key={`${rec.documentId}-${i}`} style={{ ...card, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{rec.label}</div>
                    <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>
                      From: <span style={{ color: theme.color.text }}>{rec.documentName}</span> &middot; "{rec.phrase}"
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.muted, flexShrink: 0 }}>every {rec.intervalDays} days</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
